import React, { useEffect, useState } from "react";
import { Container, Table, Spinner, Alert } from "reactstrap";
import { getCurrentUser } from "../auth/service";
import { getOrganizationMyOrders } from "../sales/service";
import { useNavigate } from "react-router-dom";

function MyOrders() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    const currentUser = getCurrentUser();

    if (!currentUser || !currentUser.org_id) {
      navigate("/login_with_otp");
      return;
    }

    const fetchMyOrders = async () => {
      setIsLoading(true);
      setErrorMessage("");
      try {
        const response = await getOrganizationMyOrders(currentUser.org_id);
        const rows = Array.isArray(response)
          ? response
          : response?.orders || response?.my_orders || response?.data || [];
        setOrders(Array.isArray(rows) ? rows : []);
      } catch (err) {
        const message = err?.error || err?.message || "Failed to load orders";
        setErrorMessage(message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMyOrders();
  }, [navigate]);

  const formatINRCurrency = (value) => {
    if (value === null || value === undefined || isNaN(Number(value))) return "-";
    try {
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 0,
      }).format(Number(value));
    } catch (_) {
      return `₹${Number(value).toLocaleString("en-IN")}`;
    }
  };

  const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (isNaN(date.getTime())) return String(value);
    return date.toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getCellStyleForDueDays = (value) => {
    if (value === null || value === undefined) return {};
    const n = Number(value);
    if (isNaN(n)) return {};
    if (n > 0) {
      return { backgroundColor: "#ff0000", color: "#ffffff" };
    }
    if (n < 0) {
      return { backgroundColor: "#39AD48", color: "#ffffff" };
    }
    return {};
  };

  const formatStatus = (status) => {
    if (!status) return "-";
    const s = String(status).replace(/_/g, " ").trim();
    return s
      .split(/\s+/)
      .map((w) => (w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : ""))
      .join(" ");
  };

  const handleOrderClick = (project_id) => {
    if (project_id) {
      navigate(`/projects/${project_id}`);
    }
  };

  return (
    <Container fluid className="p-4">
      <h4 className="mb-3">My Orders</h4>

      {isLoading && (
        <div className="d-flex align-items-center gap-2 mb-3">
          <Spinner size="sm" />
          <span>Loading orders…</span>
        </div>
      )}

      {!!errorMessage && (
        <Alert color="danger" className="mb-3">
          {errorMessage}
        </Alert>
      )}

      <div className="table-responsive">
        <Table bordered hover size="sm" className="align-middle">
          <thead>
            <tr>
              <th className="bg-secondary text-white" style={{ whiteSpace: "nowrap" }}>Customer Name</th>
              <th className="bg-secondary text-white" style={{ whiteSpace: "nowrap" }}>Order ID</th>
              <th className="bg-secondary text-white text-center" style={{ whiteSpace: "nowrap" }}>
                Status
              </th>
              <th className="bg-secondary text-white text-end" style={{ whiteSpace: "nowrap" }}>
                Order Value
              </th>
              <th className="bg-secondary text-white text-end" style={{ whiteSpace: "nowrap" }}>
                Payment Received
              </th>
              <th className="bg-secondary text-white text-end" style={{ whiteSpace: "nowrap" }}>
                Credit Period
              </th>
              <th className="bg-secondary text-white text-end" style={{ whiteSpace: "nowrap" }}>
                Balance Outstanding
              </th>
              <th className="bg-secondary text-white text-end" style={{ whiteSpace: "nowrap" }}>
                Due Days
              </th>
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 && !isLoading ? (
              <tr>
                <td colSpan={8} className="text-center text-secondary py-4">
                  No orders found
                </td>
              </tr>
            ) : (
              orders.map((order, index) => {
                const cellStyle = getCellStyleForDueDays(order.due_days);
                const rawStatus = order.status || order.order_status || order.state;
                const displayStatus = formatStatus(rawStatus);
                return (
                  <tr key={order.order_id || index} style={cellStyle}>
                    <td style={cellStyle}>{order.customer_name || "-"}</td>
                    <td style={cellStyle}>
                      {order.order_id ? (
                        <button
                          className="btn btn-link p-0 text-decoration-none"
                          style={{ 
                            color: cellStyle.color || 'inherit',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            border: 'none',
                            background: 'none'
                          }}
                          onClick={() => handleOrderClick(order.project_id)}
                          title="Click to view order details"
                        >
                          {order.order_id}
                        </button>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td className="text-center" style={cellStyle}>
                      {displayStatus}
                    </td>
                    <td className="text-end" style={cellStyle}>
                      {formatINRCurrency(order.order_value)}
                    </td>
                    <td className="text-end" style={cellStyle}>
                      {formatINRCurrency(order.payment_received)}
                    </td>
                    <td className="text-end" style={cellStyle}>
                      {formatDate(order.credit_period)}
                    </td>
                    <td className="text-end" style={cellStyle}>
                      {formatINRCurrency(order.balance_outstanding)}
                    </td>
                    <td className="text-end" style={cellStyle}>
                      {order.due_days === null || order.due_days === undefined
                        ? "-"
                        : Number(order.due_days)}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </div>
    </Container>
  );
}

export default MyOrders;
